import React, { useState } from "react";
import { Link, useNavigate } from "@/src/lib/next-compat";
import { useUser } from "@clerk/clerk-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentUserProfileApiV1UsersMeGet,
  listPublishedCoursesApiV1CoursesGet,
  getMemberPortalOverviewApiV1B2bMemberPortalOverviewGet,
} from "@/src/api/sdk.gen";
import { apiFetch } from "@/src/lib/api-fetch";
import { Sparkles, ArrowRight, BookOpen, GraduationCap, CheckCircle, HelpCircle, RotateCcw, Target, ShieldCheck, LogOut, AlertTriangle } from "lucide-react";
import ProfileNameCard from "@/components/student/ProfileNameCard";
import { startOnboardingTour } from "@/components/student/OnboardingTour";

export default function AccountPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [selectedOrgSlug, setSelectedOrgSlug] = useState<string | null>(null);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const handleLeaveOrg = async () => {
    try {
      setLeaving(true);
      setLeaveError(null);
      const res = await apiFetch("/api/v1/b2b/leave", { method: "POST" });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || errData.message || "Failed to leave organization.");
      }
      await queryClient.invalidateQueries({ queryKey: ["user-profile"] });
      await queryClient.invalidateQueries({ queryKey: ["b2b-member-portal-overview"] });
      await queryClient.invalidateQueries({ queryKey: ["published-courses"] });
      setLeaveModalOpen(false);
    } catch (err: any) {
      setLeaveError(err.message || "An error occurred while leaving the organization.");
    } finally {
      setLeaving(false);
    }
  };

  const { data: userProfile, isLoading: isProfileLoading } = useQuery({
    queryKey: ["user-profile"],
    queryFn: async () => {
      const { data } = await getCurrentUserProfileApiV1UsersMeGet();
      return data;
    },
  });
  const { data: courses, isLoading: isCoursesLoading } = useQuery({
    queryKey: ["published-courses"],
    queryFn: async () => {
      const { data } = await listPublishedCoursesApiV1CoursesGet();
      return (data || []) as any[];
    },
  });
  const hasOrganizations = (userProfile?.organizations?.length ?? 0) > 0;
  const { data: memberPortal } = useQuery({
    queryKey: ["b2b-member-portal-overview", (userProfile?.organizations || []).map((o) => o.id).join(",")],
    queryFn: async () => {
      const { data } = await getMemberPortalOverviewApiV1B2bMemberPortalOverviewGet();
      return data;
    },
    enabled: hasOrganizations,
  });

  const portalOrgs = memberPortal?.organizations || [];
  const activePortalOrg =
    portalOrgs.find((o) => o.organization_slug === selectedOrgSlug) ||
    portalOrgs[0] ||
    null;

  if (isProfileLoading || isCoursesLoading) {
    return (
      <div className="min-h-screen bg-gray-50/60 p-8 flex justify-center items-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/60">
      <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Account</h1>
          <p className="mt-1 text-sm text-gray-500">Your profile, enrolled courses, and subscription details.</p>
        </div>

        {/* User Info */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex items-center gap-4">
          <img src={user?.imageUrl} alt="Profile" className="h-14 w-14 rounded-full border border-gray-200" />
          <div>
            <h2 className="text-lg font-bold text-gray-900">{userProfile?.name || user?.fullName || user?.firstName || "Student"}</h2>
            <p className="text-sm text-gray-500">{user?.primaryEmailAddress?.emailAddress || userProfile?.email}</p>
          </div>
        </div>

        <ProfileNameCard />

        {/* Organization Workspace */}
        {userProfile?.organizations && userProfile.organizations.length > 0 && (
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Organization Workspace</h2>
            </div>
            {userProfile.organizations.map((org) => {
              const isAdminOrManager = ["admin", "owner", "manager", "superadmin"].includes(org.role?.toLowerCase() || "");
              return (
                <div
                  key={org.id || org.slug}
                  className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 via-white to-white p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-gray-900">{org.name}</h3>
                      <span
                        className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md ${
                          isAdminOrManager
                            ? "bg-indigo-100 text-indigo-700"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {org.role?.toLowerCase() === "member" ? "Employee" : org.role}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {isAdminOrManager
                        ? "Manage organization team members, course cohorts, and skill progress."
                        : "You are an enrolled employee of this organization with access to sponsored enterprise courses."}
                    </p>
                  </div>
                  {isAdminOrManager ? (
                    <Link
                      to={`/org/${org.slug}/dashboard`}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition active:scale-95 flex-shrink-0"
                    >
                      Open Dashboard
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-200 text-emerald-700 text-xs font-semibold rounded-xl shadow-xs flex-shrink-0">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                        <span>Enrolled Employee</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setLeaveModalOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 text-xs font-semibold rounded-xl transition cursor-pointer flex-shrink-0"
                      >
                        <LogOut className="h-3.5 w-3.5" />
                        <span>Leave Organization</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Leave Organization Confirmation Modal */}
            {leaveModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl border border-gray-100 space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-red-50 border border-red-100 text-red-600 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Leave Organization?</h3>
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                      Are you sure you want to leave <strong className="text-gray-800">{userProfile?.organizations?.[0]?.name || "this organization"}</strong>?
                      You will immediately forfeit access to company-sponsored courses, internal workspaces, and cohort assignments. Your personal course certificates and progression will remain safely stored.
                    </p>
                  </div>
                  {leaveError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                      {leaveError}
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-2.5 pt-3">
                    <button
                      type="button"
                      onClick={() => { setLeaveModalOpen(false); setLeaveError(null); }}
                      disabled={leaving}
                      className="px-4 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 rounded-xl border border-gray-200 hover:bg-gray-50 transition cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleLeaveOrg}
                      disabled={leaving}
                      className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-xs transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {leaving ? "Leaving..." : "Yes, Leave Organization"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activePortalOrg && (
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
                {activePortalOrg.assigned_courses && activePortalOrg.assigned_courses.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                      <BookOpen className="h-3.5 w-3.5 text-indigo-600" />
                      Assigned Courses in {activePortalOrg.organization_name}
                    </h4>
                    <div className="divide-y divide-gray-100">
                      {activePortalOrg.assigned_courses.map((ac) => (
                        <Link
                          key={`${activePortalOrg.organization_id}-${ac.course_id}`}
                          to={`/courses/${ac.course_uuid || ac.course_id}`}
                          className="flex items-center justify-between gap-4 py-2.5 hover:bg-gray-50 px-2 rounded-lg transition"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{ac.course_title}</p>
                            <p className="text-xs text-gray-500">
                              Pass: {ac.passing_threshold_score ?? 70}% • {ac.completed_lessons}/{ac.total_lessons} lessons ({ac.progress_pct ?? 0}%)
                            </p>
                          </div>
                          <span className="text-xs font-bold text-indigo-600 shrink-0">{ac.progress_pct ?? 0}%</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {activePortalOrg.skill_expectations && activePortalOrg.skill_expectations.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5 text-indigo-600" />
                      Skill Expectations
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {activePortalOrg.skill_expectations.map((sk) => (
                        <span
                          key={sk.id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-100 text-xs font-semibold text-indigo-900"
                        >
                          <span>{sk.skill_name}</span>
                          <span className="px-1.5 py-0.5 rounded bg-indigo-600 text-white text-[10px] font-bold">
                            {sk.target_proficiency}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {activePortalOrg.learning_rules && activePortalOrg.learning_rules.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                      Workspace Learning Rules
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {activePortalOrg.learning_rules.map((rule) => (
                        <span
                          key={rule.id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-100 text-xs font-semibold text-emerald-900"
                        >
                          <span>{rule.course_title || `Course #${rule.course_id}`}</span>
                          <span className="text-[10px] font-bold text-emerald-700">
                            • {rule.passing_threshold_score}% Pass
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Enrolled Courses */}
        <section className="space-y-3">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Enrolled Courses</h2>
          {courses && courses.length > 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden divide-y divide-gray-100">
              {courses.map((course) => (
                <Link
                  key={course.id}
                  to={`/courses/${course.uuid || course.id}`}
                  className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-gray-50 transition group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50">
                      <BookOpen className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate group-hover:text-indigo-600 transition">
                        {course.title}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{"All Levels"}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-indigo-400 shrink-0 transition" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-left">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 mb-3">
                <GraduationCap className="h-5 w-5 text-gray-400" />
              </div>
              <p className="font-medium text-gray-700">No courses enrolled yet</p>
              <Link
                to="/courses"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition"
              >
                Browse courses <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </section>

        {/* Subscription Plan */}
        {!hasOrganizations && (
          <section className="space-y-3">
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Subscription</h2>
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50">
                  <Sparkles className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-900">Standard Access Plan</p>
                  <p className="text-xs text-gray-500">Access to all active learning modules</p>
                </div>
              </div>
              <Link
                to="/account/pricing"
                className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
              >
                Manage Plans <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </section>
        )}

        {/* Platform Tour & Help */}
        <section className="space-y-3">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Help & Platform Tour</h2>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 shrink-0">
                <HelpCircle className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-base font-bold text-gray-900">Interactive Onboarding Tour</p>
                <p className="text-xs text-gray-500">Replay the platform walkthrough covering courses, quizzes, and certificates.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => startOnboardingTour(navigate)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition active:scale-95 shrink-0"
            >
              <RotateCcw className="h-4 w-4" />
              Replay Tour
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
